import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Users, ShieldCheck, Wallet, BadgePercent, Copy, LogOut, Search, Eye, Loader2 } from 'lucide-react';
import { SalesClientDetail, kycBadge } from '@/components/sales/SalesClientDetail';
import { SalesOnboarding } from '@/components/sales/onboarding/SalesOnboarding';
import { InviteClientsTab } from '@/components/sales/InviteClientsTab';

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const TH = 'text-left py-3 px-4 text-xs uppercase tracking-wider font-semibold text-brand-text-secondary';

function Stat({ label, value, icon: Icon, testid }) {
  return (
    <Card className="border border-brand-border bg-white">
      <CardContent className="p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-brand-bg flex items-center justify-center"><Icon size={18} className="text-brand-primary" /></div>
        <div><p className="text-[10px] uppercase tracking-widest font-semibold text-brand-text-secondary">{label}</p>
          <p className="font-heading text-xl font-bold text-brand-text-primary" data-testid={testid}>{value}</p></div>
      </CardContent>
    </Card>
  );
}

function ClientsTable({ clients, onOpen }) {
  const [q, setQ] = useState('');
  const rows = clients.filter(c => `${c.name} ${c.client_code} ${c.email} ${c.phone}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <Card className="border border-brand-border bg-white">
      <div className="p-3 border-b border-brand-border relative max-w-sm">
        <Search size={14} className="absolute left-6 top-1/2 -translate-y-1/2 text-brand-text-secondary" />
        <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, ID, email" className="pl-8 h-9" data-testid="sales-client-search" />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="sales-clients-table">
          <thead><tr className="border-b border-brand-border bg-brand-bg/50">
            {['Client ID', 'Name', 'KYC', 'Deposits', 'Withdrawals', 'Profit', 'Main Balance', ''].map(h => <th key={h} className={TH}>{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={8} className="py-12 text-center text-brand-text-secondary">No clients yet. Share your code to onboard clients.</td></tr> : rows.map(c => (
              <tr key={c.id} className="border-b border-brand-border/50 hover:bg-brand-surface-hover transition-colors">
                <td className="py-3 px-4 font-mono text-xs text-brand-primary">{c.client_code}</td>
                <td className="py-3 px-4"><button onClick={() => onOpen(c.id)} className="font-medium text-brand-text-primary hover:text-brand-primary hover:underline text-left" data-testid={`sales-client-name-${c.id}`}>{c.name}</button><p className="text-xs text-brand-text-secondary">{c.email}</p></td>
                <td className="py-3 px-4">{kycBadge(c.kyc_status)}</td>
                <td className="py-3 px-4">{money(c.deposits)}</td>
                <td className="py-3 px-4">{money(c.withdrawals)}</td>
                <td className={`py-3 px-4 font-medium ${c.profit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{money(c.profit)}</td>
                <td className="py-3 px-4 font-semibold">{money(c.main_balance)}</td>
                <td className="py-3 px-4"><Button size="sm" variant="outline" onClick={() => onOpen(c.id)} className="h-8 text-xs" data-testid={`sales-view-client-${c.id}`}><Eye size={13} className="mr-1" /> View</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function CommissionTable({ rows }) {
  return (
    <Card className="border border-brand-border bg-white overflow-x-auto">
      <table className="w-full text-sm" data-testid="sales-commissions-table">
        <thead><tr className="border-b border-brand-border bg-brand-bg/50">
          {['Month', 'Client', 'Client Net Profit', 'Rate', 'Commission', 'Status'].map(h => <th key={h} className={TH}>{h}</th>)}
        </tr></thead>
        <tbody>
          {rows.length === 0 ? <tr><td colSpan={6} className="py-12 text-center text-brand-text-secondary">No commission records yet</td></tr> : rows.map(r => (
            <tr key={r.id} className="border-b border-brand-border/50">
              <td className="py-3 px-4 font-mono text-xs">{r.month}</td>
              <td className="py-3 px-4">{r.client_name} <span className="font-mono text-xs text-brand-primary ml-1">{r.client_code}</span></td>
              <td className="py-3 px-4">{money(r.client_net_profit)}</td>
              <td className="py-3 px-4">{r.commission_pct}%</td>
              <td className="py-3 px-4 font-semibold text-emerald-700">{money(r.commission)}</td>
              <td className="py-3 px-4"><Badge className={`border-0 text-[11px] capitalize ${r.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{r.status}</Badge></td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

export default function SalesPortal() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [clients, setClients] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [status, setStatus] = useState(null);
  const closeDetail = useCallback(() => setOpenId(null), []);

  const loadStatus = useCallback(() => {
    api.get('/sales/onboarding/status').then(r => setStatus(r.data))
      .catch(err => toast.error(formatError(err.response?.data?.detail)));
  }, []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  const verified = status?.onboarding_status === 'verified';
  useEffect(() => {
    if (!verified) return;
    Promise.all([api.get('/sales/me/summary'), api.get('/sales/me/clients'), api.get('/sales/me/commissions')])
      .then(([s, c, m]) => { setSummary(s.data); setClients(c.data); setCommissions(m.data); })
      .catch(err => toast.error(formatError(err.response?.data?.detail)));
  }, [verified]);

  const code = summary?.sales_code || user?.sales_code;
  const link = `${window.location.origin}/register?sp=${code}`;
  const copy = () => { navigator.clipboard.writeText(link); toast.success('Registration link copied'); };
  const doLogout = async () => { await logout(); navigate('/login'); };

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="sales-portal">
      <header className="bg-[#0F1729] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-5 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/quantora-logo.png" alt="Quantora" className="w-9 h-9 rounded-lg" />
            <div><p className="text-[10px] uppercase tracking-[0.25em] text-white/50">Sales Partner</p>
              <p className="font-heading font-bold" data-testid="sales-name">{summary?.name || user?.name}</p></div>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm bg-white/10 border border-white/15 rounded-lg px-3 py-1.5" data-testid="sales-code">{code}</span>
            {verified && <Button size="sm" onClick={copy} className="bg-blue-500 hover:bg-blue-600 text-white h-9" data-testid="copy-sales-link-btn"><Copy size={14} className="mr-1.5" /> Copy Link</Button>}
            <Button size="sm" variant="ghost" onClick={doLogout} className="text-white/70 hover:text-white hover:bg-white/10 h-9" data-testid="sales-logout-btn"><LogOut size={15} /></Button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-8 space-y-6">
        {!status ? <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-brand-primary" /></div>
          : !verified ? <SalesOnboarding status={status} onChange={loadStatus} /> : (<>
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-brand-text-primary tracking-tight">My Clients</h1>
          <p className="text-sm text-brand-text-secondary mt-1">You earn {summary?.commission_pct ?? 5}% of each client&apos;s net monthly profit. Clients must register with your code.</p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat label="Clients" value={summary?.client_count ?? 0} icon={Users} testid="stat-clients" />
          <Stat label="KYC Verified" value={`${summary?.kyc?.verified ?? 0} / ${summary?.client_count ?? 0}`} icon={ShieldCheck} testid="stat-kyc" />
          <Stat label="Client Balances" value={money(summary?.total_balance)} icon={Wallet} testid="stat-balance" />
          <Stat label="Commission Pending" value={money(summary?.commission_pending)} icon={BadgePercent} testid="stat-commission-pending" />
        </div>
        <Tabs defaultValue="clients" className="space-y-4">
          <TabsList className="bg-white border border-brand-border rounded-lg p-0.5">
            <TabsTrigger value="clients" className="text-xs px-4" data-testid="tab-sales-clients">Clients</TabsTrigger>
            <TabsTrigger value="commissions" className="text-xs px-4" data-testid="tab-sales-commissions">Commissions · Paid {money(summary?.commission_paid)}</TabsTrigger>
            <TabsTrigger value="invite" className="text-xs px-4" data-testid="tab-sales-invite">Invite Clients</TabsTrigger>
          </TabsList>
          <TabsContent value="clients"><ClientsTable clients={clients} onOpen={setOpenId} /></TabsContent>
          <TabsContent value="commissions"><CommissionTable rows={commissions} /></TabsContent>
          <TabsContent value="invite"><InviteClientsTab code={code} /></TabsContent>
        </Tabs>
        </>)}
      </main>
      <SalesClientDetail clientId={openId} onClose={closeDetail} />
    </div>
  );
}
